import type { DeviceInfo, MediaRecorderState } from 'js/src/components/app/recorder/types';
import { MimeType, RecordingType } from 'js/src/components/app/recorder/types';
import { stopAndRemoveAllTracksFromStream } from 'js/src/libs/recorder/recorder-utils';
import Stopwatch from 'js/src/libs/stopwatch';
import uuid from 'js/src/libs/uuid';

interface BaseRecorderInterface {
    checkSupport(): void;
    checkPermissions(): Promise<void>;
    initialize(): Promise<void>;
    startRecording(): Promise<MediaRecorderState>;
    pauseRecording(): Promise<MediaRecorderState>;
    resumeRecording(): Promise<MediaRecorderState>;
    finishRecording(): Promise<{ previewUrl: string; state: MediaRecorderState }>;
    retakeRecording(): Promise<MediaRecorderState>;
    connectMicrophone(deviceId?: string): Promise<void>;
    disconnectMicrophone(): void;
    connectCamera(deviceId?: string): Promise<void>;
    disconnectCamera(): void;
    connectScreen(deviceId?: string): Promise<void>;
    disconnectScreen(): void;
    destroy(): void;
}

export class BaseRecorder implements BaseRecorderInterface {
    protected instanceId: string;
    protected recordingType = RecordingType.AUDIO;

    mimeType: MimeType | null = null;
    recordedBlob: Blob[] = [];
    #previewBlob: Blob | null = null;
    #previewUrl: string | null = null;

    #mediaRecorder: MediaRecorder | null = null;
    protected mediaRecorderStream: MediaStream | null = null;

    /**
     * Listeners notified each time the MediaRecorder emits a chunk. Used by
     * streaming-upload code to forward chunks to GCS as they arrive, without
     * waiting for the full recording to finish.
     */
    #dataAvailableListeners = new Set<(blob: Blob) => void>();

    private internalClock = new Stopwatch();

    constructor() {
        this.instanceId = uuid();
    }

    #availableMicrophoneDevices: DeviceInfo[] = [];
    #defaultMicrophoneDeviceId: string | undefined = undefined;
    #activeMicrophoneDeviceId: string | undefined = undefined;

    #availableCameraDevices: DeviceInfo[] = [];
    #defaultCameraDeviceId: string | undefined = undefined;
    #activeCameraDeviceId: string | undefined = undefined;

    #availableScreenDevices: DeviceInfo[] = [];
    #defaultScreenDeviceId: string | undefined = undefined;
    #activeScreenDeviceId: string | undefined = undefined;

    get liveStream() {
        return this.mediaRecorderStream;
    }

    get previewBlob() {
        return this.#previewBlob;
    }

    get previewUrl() {
        return this.#previewUrl;
    }

    get recordedDuration() {
        return this.internalClock.totalDuration;
    }

    get mediaRecorderState(): MediaRecorderState {
        return this.#mediaRecorder?.state ?? 'inactive';
    }

    /**
     * Subscribe to incremental MediaRecorder chunks. The callback fires once per
     * `dataavailable` event (currently every 1 s — see the Safari workaround in
     * `startRecording`). Returns an unsubscribe function.
     */
    public onDataAvailable(listener: (blob: Blob) => void): () => void {
        this.#dataAvailableListeners.add(listener);
        return () => {
            this.#dataAvailableListeners.delete(listener);
        };
    }

    get availableMicrophoneDevices(): DeviceInfo[] {
        return this.#availableMicrophoneDevices;
    }

    protected set availableMicrophoneDevices(devices: DeviceInfo[]) {
        this.#availableMicrophoneDevices = devices;
    }

    get defaultMicrophoneDeviceId(): string | undefined {
        return this.#defaultMicrophoneDeviceId;
    }

    protected set defaultMicrophoneDeviceId(deviceId: string | undefined) {
        this.#defaultMicrophoneDeviceId = deviceId;
    }

    get activeMicrophoneDeviceId(): string | undefined {
        return this.#activeMicrophoneDeviceId;
    }

    protected set activeMicrophoneDeviceId(deviceId: string | undefined) {
        this.#activeMicrophoneDeviceId = deviceId;
    }

    get availableCameraDevices(): DeviceInfo[] {
        return this.#availableCameraDevices;
    }

    protected set availableCameraDevices(devices: DeviceInfo[]) {
        this.#availableCameraDevices = devices;
    }

    get defaultCameraDeviceId(): string | undefined {
        return this.#defaultCameraDeviceId;
    }

    protected set defaultCameraDeviceId(deviceId: string | undefined) {
        this.#defaultCameraDeviceId = deviceId;
    }

    get activeCameraDeviceId(): string | undefined {
        return this.#activeCameraDeviceId;
    }

    protected set activeCameraDeviceId(deviceId: string | undefined) {
        this.#activeCameraDeviceId = deviceId;
    }

    get availableScreenDevices(): DeviceInfo[] {
        return this.#availableScreenDevices;
    }

    protected set availableScreenDevices(devices: DeviceInfo[]) {
        this.#availableScreenDevices = devices;
    }

    get defaultScreenDeviceId(): string | undefined {
        return this.#defaultScreenDeviceId;
    }

    protected set defaultScreenDeviceId(deviceId: string | undefined) {
        this.#defaultScreenDeviceId = deviceId;
    }

    get activeScreenDeviceId(): string | undefined {
        return this.#activeScreenDeviceId;
    }

    protected set activeScreenDeviceId(deviceId: string | undefined) {
        this.#activeScreenDeviceId = deviceId;
    }

    get isMicrophoneConnected(): boolean {
        return false;
    }

    /**
     * The live microphone audio track, if one is connected. Exposed so live
     * transcription can consume the already-open mic instead of opening its own.
     */
    get micTrack(): MediaStreamTrack | null {
        return null;
    }

    get isCameraConnected(): boolean {
        return false;
    }

    /**
     * Whether system/tab audio will be captured alongside the recording. Only
     * meaningful for screen recording; other recorders never capture it.
     */
    get isSystemAudioCaptureEnabled(): boolean {
        return false;
    }

    /**
     * Whether a live system-audio track is actually being captured right now.
     * This can be false even when {@link isSystemAudioCaptureEnabled} is true —
     * e.g. the shared source (a window or whole screen) provided no audio track.
     * Used to warn the user that their requested system audio isn't happening.
     */
    get isSystemAudioActive(): boolean {
        return false;
    }

    setCaptureSystemAudio(_enabled: boolean): void {
        // Only screen recording captures system audio; no-op by default.
    }

    checkSupport() {
        if (!window.MediaRecorder) {
            throw new Error('MediaRecorder is not supported in this browser');
        }
        if (!navigator) {
            throw new Error('Navigator is not available in this environment.');
        }
        if (!navigator.mediaDevices) {
            throw new Error('Navigator.mediaDevices is not available in this environment.');
        }
        if (!('enumerateDevices' in navigator.mediaDevices)) {
            throw new Error(
                'Navigator.mediaDevices.enumerateDevices is not available in this environment.',
            );
        }
    }

    async checkPermissions() {
        return Promise.resolve();
    }

    private getSupportedMimeTypeForMediaRecorder(): MimeType | null {
        if (!window.MediaRecorder.isTypeSupported) {
            return null;
        }
        if (this.recordingType === RecordingType.AUDIO) {
            if (window.MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
                return MimeType.FIREFOX_AUDIO_MIME_TYPE;
            } else if (window.MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                return MimeType.CHROME_OPERA_AUDIO_MIME_TYPE;
            } else if (window.MediaRecorder.isTypeSupported('audio/mp4')) {
                return MimeType.SAFARI_AUDIO_MIME_TYPE;
            } else {
                return null;
            }
        }
        if (this.recordingType === RecordingType.VIDEO) {
            if (window.MediaRecorder.isTypeSupported(MimeType.CHROME_VIDEO_MIME_TYPE)) {
                return MimeType.CHROME_VIDEO_MIME_TYPE;
            } else if (window.MediaRecorder.isTypeSupported(MimeType.FIREFOX_VIDEO_MIME_TYPE)) {
                return MimeType.FIREFOX_VIDEO_MIME_TYPE;
            } else if (window.MediaRecorder.isTypeSupported(MimeType.SAFARI_VIDEO_MIME_TYPE)) {
                return MimeType.SAFARI_VIDEO_MIME_TYPE;
            } else {
                return null;
            }
        }
        return null;
    }

    public async initialize() {
        this.destroy();
        this.resetState();

        this.mimeType = this.getSupportedMimeTypeForMediaRecorder();

        this.mediaRecorderStream = new MediaStream();
        const mediaRecorder = new MediaRecorder(this.mediaRecorderStream, {
            mimeType: this.mimeType || undefined,
        });
        mediaRecorder.ondataavailable = (e: BlobEvent) => {
            if (e.data.size > 0) {
                this.recordedBlob.push(e.data);
                for (const listener of this.#dataAvailableListeners) {
                    listener(e.data);
                }
            }
        };
        mediaRecorder.onerror = (e: Event) => {
            throw e;
        };
        this.#mediaRecorder = mediaRecorder;

        await this.initializeAvailableDevices();
    }

    async initializeAvailableDevices() {
        await this.initializeMicrophoneDevices();
        await this.initializeCameraDevices();
        await this.initializeScreenDevices();
    }

    protected async initializeMicrophoneDevices() {
        this.availableMicrophoneDevices = [];
        this.defaultMicrophoneDeviceId = undefined;
        this.activeMicrophoneDeviceId = undefined;
        return Promise.resolve();
    }

    protected async initializeCameraDevices() {
        this.availableCameraDevices = [];
        this.defaultCameraDeviceId = undefined;
        this.activeCameraDeviceId = undefined;
        return Promise.resolve();
    }

    protected async initializeScreenDevices() {
        this.availableScreenDevices = [];
        this.defaultScreenDeviceId = undefined;
        this.activeScreenDeviceId = undefined;
        return Promise.resolve();
    }

    addTrackToMediaRecorderStream(track: MediaStreamTrack) {
        if (!this.mediaRecorderStream) {
            throw new Error('MediaRecorder stream is not initialized');
        }
        this.mediaRecorderStream.addTrack(track);
    }

    removeTrackFromMediaRecorderStream(track: MediaStreamTrack) {
        if (!this.mediaRecorderStream) {
            throw new Error('MediaRecorder stream is not initialized');
        }
        this.mediaRecorderStream.removeTrack(track);
    }

    public startRecording(): Promise<MediaRecorderState> {
        this.recordedBlob = [];
        return new Promise<MediaRecorderState>((resolve, reject) => {
            if (!this.#mediaRecorder) {
                reject(new Error('MediaRecorder is not initialized'));
                return;
            }
            if (this.#mediaRecorder.state === 'recording') {
                resolve(this.mediaRecorderState);
                return;
            }

            this.#mediaRecorder.onstart = () => {
                resolve(this.mediaRecorderState);
            };
            /**
             * SAFARI BUG
             *
             * In the spec, the argument to MediaRecorder.start() is optional.
             * But if we don't supply one, Safari produces files that the
             * backend thinks are only (just under) one second in duration.
             * FFprobe cannot find the correct duration of these broken files.
             * Supplying a timeSlice of 1000ms fixes this issue.
             *
             * https://community.openai.com/t/whisper-problem-with-audio-mp4-blobs-from-safari/322252
             */
            this.#mediaRecorder.start(1000);
            this.internalClock.start();
        });
    }

    public pauseRecording(): Promise<MediaRecorderState> {
        return new Promise<MediaRecorderState>((resolve, reject) => {
            if (!this.#mediaRecorder) {
                reject(new Error('MediaRecorder is not initialized'));
                return;
            }
            if (this.#mediaRecorder.state === 'paused') {
                resolve(this.mediaRecorderState);
                return;
            }
            this.#mediaRecorder.onpause = () => {
                resolve(this.mediaRecorderState);
            };
            this.#mediaRecorder.pause();
            this.internalClock.pause();
        });
    }

    public resumeRecording(): Promise<MediaRecorderState> {
        return new Promise<MediaRecorderState>((resolve, reject) => {
            if (!this.#mediaRecorder) {
                reject(new Error('MediaRecorder is not initialized'));
                return;
            }
            if (this.#mediaRecorder.state === 'recording') {
                resolve(this.mediaRecorderState);
                return;
            }
            this.#mediaRecorder.onresume = () => {
                resolve(this.mediaRecorderState);
            };
            this.#mediaRecorder.resume();
            this.internalClock.start();
        });
    }

    private stopRecording(): Promise<MediaRecorderState> {
        return new Promise<MediaRecorderState>((resolve) => {
            this.internalClock.stop();
            if (this.#mediaRecorder === null || this.#mediaRecorder.state === 'inactive') {
                this.destroy();
                resolve(this.mediaRecorderState);
                return;
            }
            this.#mediaRecorder.onstop = () => {
                this.destroy();
                resolve(this.mediaRecorderState);
            };
            this.#mediaRecorder.stop();
        });
    }

    public async finishRecording(): Promise<{ previewUrl: string; state: MediaRecorderState }> {
        const state = await this.stopRecording();
        const previewUrl = this.generatePreviewUrl();
        return { previewUrl, state };
    }

    public async retakeRecording(): Promise<MediaRecorderState> {
        return this.stopRecording();
    }

    private generatePreviewUrl(): string {
        if (this.mimeType === null) {
            throw new Error('No supported mime type for MediaRecorder');
        }
        if (!this.recordedBlob.length) {
            throw new Error('No data was captured during recording');
        }
        this.#previewBlob = new Blob(this.recordedBlob, { type: this.mimeType });
        if (this.#previewUrl) {
            URL.revokeObjectURL(this.#previewUrl);
            this.#previewUrl = null;
        }
        this.#previewUrl = URL.createObjectURL(this.#previewBlob);
        return this.#previewUrl;
    }

    private resetState(): void {
        if (this.#previewUrl) {
            URL.revokeObjectURL(this.#previewUrl);
            this.#previewUrl = null;
        }
        this.#previewBlob = null;
        this.recordedBlob = [];
        this.internalClock.reset();

        this.#availableMicrophoneDevices = [];
        this.#defaultMicrophoneDeviceId = undefined;
        this.#activeMicrophoneDeviceId = undefined;

        this.#availableCameraDevices = [];
        this.#defaultCameraDeviceId = undefined;
        this.#activeCameraDeviceId = undefined;

        this.#availableScreenDevices = [];
        this.#defaultScreenDeviceId = undefined;
        this.#activeScreenDeviceId = undefined;
    }

    connectMicrophone(_deviceId?: string): Promise<void> {
        return Promise.resolve();
    }

    disconnectMicrophone() {
        // do nothing
    }

    connectCamera(_deviceId?: string): Promise<void> {
        return Promise.resolve();
    }

    disconnectCamera() {
        // do nothing
    }

    connectScreen(_deviceId?: string): Promise<void> {
        return Promise.resolve();
    }

    disconnectScreen() {
        // do nothing
    }

    private destroyMediaRecorder() {
        if (this.#mediaRecorder) {
            if (this.#mediaRecorder.state !== 'inactive') {
                this.#mediaRecorder.stop();
            }
            this.#mediaRecorder = null;
        }
    }

    private destroyMediaRecorderStream() {
        stopAndRemoveAllTracksFromStream(this.mediaRecorderStream);
        this.mediaRecorderStream = null;
    }

    public destroy() {
        this.destroyMediaRecorder();
        this.destroyMediaRecorderStream();
    }
}
